/**
 * RescheduleEditor — a small inline "change the date & time" control reused by
 * every scheduling surface (Calendar drawer, Videos library / Job Detail, Posts
 * library cards). Wraps the brand `BrandDateTimePicker` with Save / Cancel and
 * busy + error handling.
 *
 * Contract:
 *   - `currentISO` is the item's existing schedule time (UTC ISO-8601).
 *   - the picker works in the browser's LOCAL wall-clock ("YYYY-MM-DDTHH:MM");
 *     on Save we convert back to a UTC ISO instant — the exact same round-trip
 *     `SchedulePostModal` uses — and hand it to `onSave`.
 *   - `onSave` performs the actual reschedule (Buffer + any local sync) and
 *     should close/unmount this editor on success. On failure it should reject;
 *     we surface the message and re-enable the form.
 *
 * The picker's own `min` is "now", so a post can never be moved into the past.
 */
import { useState } from "react";
import { BrandDateTimePicker } from "./BrandDateTimePicker";

const pad = (n: number): string => String(n).padStart(2, "0");

/** UTC ISO → local "YYYY-MM-DDTHH:MM" (the picker's shape). */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Local "YYYY-MM-DDTHH:MM" → UTC ISO instant (null on invalid). */
function localToISO(local: string): string | null {
  if (!local) return null;
  const d = new Date(`${local}:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function nowLocalInput(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function RescheduleEditor({
  currentISO,
  onSave,
  onCancel,
  saveLabel = "Save time",
  note,
}: {
  currentISO: string;
  onSave: (iso: string) => Promise<void>;
  onCancel: () => void;
  saveLabel?: string;
  /** Optional one-line hint under the picker (e.g. "moves all 3 channels"). */
  note?: string;
}): React.JSX.Element {
  const [local, setLocal] = useState(() => isoToLocalInput(currentISO) || nowLocalInput());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const iso = localToISO(local);
  // Within a minute of the current time = no real change (the picker is
  // minute-precision), so keep Save disabled to avoid a pointless Buffer write.
  const unchanged =
    iso !== null &&
    !!currentISO &&
    Math.abs(new Date(iso).getTime() - new Date(currentISO).getTime()) < 60_000;

  const save = async (): Promise<void> => {
    if (!iso || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(iso);
      // Success → the parent closes this editor; leave the button disabled so a
      // second click can't double-submit during unmount.
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <BrandDateTimePicker value={local} onChange={setLocal} min={nowLocalInput()} disabled={busy} />
      {note && (
        <div style={{ fontSize: 11.5, color: "var(--ql-fg-muted, #9aa)", lineHeight: 1.4 }}>{note}</div>
      )}
      {error && (
        <div style={{ fontSize: 12, color: "#f2a5a5", lineHeight: 1.4 }}>{error}</div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={save}
          disabled={busy || !iso || unchanged}
          style={{
            flex: 1,
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid rgba(255,213,0,.5)",
            background: busy || unchanged ? "rgba(255,213,0,.12)" : "var(--ql-yellow, #ffd500)",
            color: busy || unchanged ? "var(--ql-fg-muted, #9aa)" : "#1a1a1a",
            fontWeight: 700,
            fontSize: 12.5,
            cursor: busy || unchanged ? "default" : "pointer",
            fontFamily: "inherit",
          }}
        >
          {busy ? "Saving…" : saveLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,.14)",
            background: "transparent",
            color: "var(--ql-fg, #eaeaea)",
            fontWeight: 600,
            fontSize: 12.5,
            cursor: busy ? "default" : "pointer",
            fontFamily: "inherit",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
